import React from "react";
import { LineChart, TrendingUp, TrendingDown, Ship, ArrowRight } from "lucide-react";
import { Hero, Section, Sub, Callout, Bold } from "./_shared";
import { useT } from "@/i18n";

export default function PricingPage() {
  const { lang } = useT();
  return lang === "en" ? <PricingEN /> : <PricingFR />;
}

// =============================================================
// FR
// =============================================================
function PricingFR() {
  return (
    <div data-testid="view-pricing" className="max-w-5xl">
      <Hero
        moduleNum="04"
        eyebrowLabel="Module"
        icon={LineChart}
        italic="Pricing"
        accent="& valorisation"
        description="Savoir lire les marchés est une compétence que la plupart des intermédiaires débutants n'ont pas. Sans elle, vous ne pouvez pas évaluer si un deal est réaliste, négocier efficacement les prix, ni détecter les arnaques basées sur des prix fictifs."
      />

      <Section eyebrow="Prix spot" title="Lire les prix spot" index="4.1">
        <Sub title="Qu'est-ce que le prix spot ?">
          <p>
            Le prix spot (ou prix au comptant) est le prix auquel une marchandise peut être achetée ou vendue pour une
            <Bold> livraison immédiate</Bold> — généralement dans les 2 jours ouvrables. Il représente la valeur de
            marché en temps réel, reflétant l'offre et la demande instantanées. À ne pas confondre avec les prix à terme
            (futures) qui concernent des livraisons à une date future déterminée et sont utilisés principalement pour
            se couvrir contre les variations de prix.
          </p>
        </Sub>

        <Sub title="Sources de prix par matière première">
          <ul className="space-y-3">
            <li><Bold>Or et métaux précieux :</Bold> London Bullion Market Association (lbma.org.uk). Publie deux fixings
            par jour (AM et PM, en semaine). Le LBMA Gold Price PM est la référence mondiale utilisée dans tous les
            contrats sérieux. Prix exprimé en USD par once troy (1 once troy = 31,1035 grammes).</li>
            <li><Bold>Argent :</Bold> même source LBMA. Un seul fixing par jour (PM).</li>
            <li><Bold>Platine et palladium :</Bold> London Platinum and Palladium Market (lppm.com).</li>
            <li><Bold>Pétrole brut Brent</Bold> (référence européenne et mondiale) : ICE Futures Europe. Prix exprimé en
            USD par baril (1 baril = 158,99 litres). Les agences Platts (S&P Global) et Argus Media sont les références
            professionnelles. Bloomberg et Reuters retransmettent ces prix en temps réel.</li>
            <li><Bold>Pétrole brut WTI</Bold> (référence américaine) : NYMEX (CME Group). Prix légèrement différent du
            Brent selon les conditions de marché.</li>
            <li><Bold>Métaux industriels</Bold> (cuivre, aluminium, zinc, plomb, nickel, étain) : London Metal Exchange
            (lme.com). Cotations officielles à 3 mois très suivies. Prix exprimé en USD par tonne métrique.</li>
            <li><Bold>Produits agricoles :</Bold> blé, maïs, soja sur le CBOT (Chicago Board of Trade, groupe CME). Café
            arabica et cacao sur ICE Futures US. Sucre brut sur ICE Futures US. Café robusta sur ICE Futures Europe (Londres).</li>
            <li><Bold>Ciment :</Bold> pas de marché coté centralisé mondial. Les prix sont régionaux et se consultent via
            Global Cement Magazine, CemNet, CemBureau, ou les indices Platts Ciment pour certaines régions.</li>
            <li><Bold>Pierres précieuses (diamants) :</Bold> Rapaport Price List (rappaport.com). Publication hebdomadaire
            en USD par carat selon la qualité (cut, color, clarity, carat — les 4C). Pour les pierres colorées (émeraudes,
            rubis, saphirs) : GemGuide ou les prix publiés par les maisons de vente aux enchères (Sotheby's, Christie's).</li>
            <li><Bold>Gaz naturel :</Bold> Henry Hub (NYMEX, référence nord-américaine). TTF (Title Transfer Facility,
            référence européenne, coté sur ICE). NBP (National Balancing Point, référence britannique).</li>
          </ul>
        </Sub>

        <Sub title="Comment lire une cotation Bloomberg ou Reuters">
          <ul className="space-y-2">
            <li><Bold>Last Price (ou Last) :</Bold> le dernier prix auquel une transaction a été réalisée.</li>
            <li><Bold>Bid :</Bold> le prix auquel un acheteur est prêt à acheter immédiatement.</li>
            <li><Bold>Ask (ou Offer) :</Bold> le prix auquel un vendeur est prêt à vendre immédiatement.</li>
            <li><Bold>Spread :</Bold> la différence entre Bid et Ask. Un spread faible indique un marché liquide. Un
            spread élevé indique un marché peu liquide ou tendu.</li>
            <li><Bold>High / Low :</Bold> le plus haut et le plus bas de la journée. Permet d'évaluer la volatilité intraday.</li>
            <li><Bold>Volume :</Bold> le nombre de contrats ou de lots échangés. Un volume élevé confirme la liquidité et
            la validité des prix.</li>
            <li><Bold>Change (ou % Change) :</Bold> la variation par rapport à la clôture de la veille, en valeur absolue
            et en pourcentage.</li>
            <li><Bold>Open Interest</Bold> (pour les marchés à terme uniquement) : le nombre total de contrats ouverts
            (ni expirés ni liquidés). Un Open Interest croissant indique un intérêt croissant du marché sur ce niveau de prix.</li>
          </ul>
        </Sub>
      </Section>

      <Section eyebrow="Primes & décotes" title="Comprendre les primes et décotes" index="4.2">
        <Sub title="Définition">
          <p>
            Le prix spot est un prix de référence théorique standard. En pratique, presque toutes les transactions se
            réalisent avec une <Bold>prime (premium)</Bold> ou une <Bold>décote (discount)</Bold> par rapport à ce prix
            de référence, en fonction de multiples facteurs liés à la qualité, à la logistique, au marché local et aux
            conditions de la transaction.
          </p>
        </Sub>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <div className="border border-[#25D366]/30 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><TrendingUp className="h-5 w-5 text-[#25D366]" /><h4 className="font-serif text-lg text-[#25D366]">Facteurs créant une prime (+)</h4></div>
            <ul className="mt-5 space-y-3 text-sm text-[#C4C4C7]">
              <li><Bold>Qualité supérieure au standard de référence :</Bold> un cuivre de pureté 99,99 % se vend avec une prime par rapport au cuivre LME Grade A (standard 99,97 %). De même, un or de pureté 999,9/1000 se vend avec une prime par rapport à un or 995/1000.</li>
              <li><Bold>Certification et traçabilité :</Bold> un or certifié Fairmined (issu de mines artisanales certifiées responsables) se vend avec une prime significative sur certains marchés européens et nord-américains.</li>
              <li><Bold>Livraison sur site spécifique ou délai raccourci :</Bold> si vous pouvez livrer CIF dans un port difficile d'accès ou dans un délai plus court que la norme, vous pouvez justifier une prime.</li>
              <li><Bold>Marché local tendu :</Bold> si la demande dépasse l'offre dans une région spécifique, les prix locaux dépassent le spot mondial.</li>
              <li><Bold>Garanties additionnelles :</Bold> une garantie bancaire SBLC ou une assurance cargo premium peut justifier une prime sur le prix de base.</li>
            </ul>
          </div>
          <div className="border border-[#FF3B30]/30 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><TrendingDown className="h-5 w-5 text-[#FF3B30]" /><h4 className="font-serif text-lg text-[#FF3B30]">Facteurs créant une décote (−)</h4></div>
            <ul className="mt-5 space-y-3 text-sm text-[#C4C4C7]">
              <li>Qualité légèrement inférieure au standard de référence.</li>
              <li><Bold>Destination à risque élevé :</Bold> une livraison dans un port ou un pays avec des complications logistiques, des frais portuaires élevés, ou un risque politique fort.</li>
              <li>Gros volumes nécessitant du temps pour être absorbés par le marché.</li>
              <li>Longue chaîne d'intermédiaires augmentant le risque de contrepartie perçu.</li>
              <li><Bold>Incertitude sur l'origine ou la traçabilité :</Bold> un produit sans certificat d'origine clair se vend avec une décote.</li>
            </ul>
          </div>
        </div>

        <Sub title="Exemple de calcul complet pour 1 tonne d'or">
          <div className="border border-[#D4AF37]/30 bg-[#0c0c0c] p-6 md:p-8 font-mono text-sm">
            <div className="space-y-3">
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Prix spot LBMA du jour</span><span className="text-white">2 350 $ / oz troy</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Conversion en USD/kg</span><span className="text-white">2 350 ÷ 31,1035 × 1 000 = 75 560 $/kg</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Prix de base · 1 tonne (1 000 kg)</span><span className="text-white">75 560 000 $</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Prime qualité + certification (+1,5 %)</span><span className="text-[#25D366]">+ 1 133 400 $</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Commission intermédiaire (1 %)</span><span className="text-[#D4AF37]">+ 756 934 $</span></div>
              <div className="h-px bg-white/10 my-3" />
              <div className="flex justify-between font-bold"><span className="text-white">Prix CIF total acheteur</span><span className="text-[#D4AF37]">~ 78 450 000 $</span></div>
            </div>
          </div>
          <p className="mt-4 italic text-sm">
            Concrètement, sur ce deal, la commission de 1 % représente environ <Bold>756 934 $</Bold> pour un deal d'une
            tonne d'or. C'est pourquoi ce secteur attire autant d'escrocs : les montants sont énormes et les marges
            paraissent très attractives.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Incoterms" title="Impact FOB vs CIF sur la commission de l'intermédiaire" index="4.3">
        <p>
          Choisir le bon incoterm n'est pas seulement une question logistique — c'est aussi une décision qui
          <Bold> impacte directement le montant de votre commission</Bold>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><Ship className="h-5 w-5 text-[#D4AF37]" /><span className="font-mono text-xl text-[#D4AF37]">FOB</span></div>
            <p className="font-serif text-sm text-[#A1A1AA] mt-1">Free On Board</p>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Le vendeur livre la marchandise à bord du navire au port d'embarquement. Les frais de fret maritime et
              d'assurance voyage sont à la charge de l'acheteur. <Bold>Le prix FOB ne comprend donc pas ces coûts.</Bold>
            </p>
          </div>
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><Ship className="h-5 w-5 text-[#D4AF37]" /><span className="font-mono text-xl text-[#D4AF37]">CIF</span></div>
            <p className="font-serif text-sm text-[#A1A1AA] mt-1">Cost, Insurance, Freight</p>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Le vendeur livre la marchandise au port de destination en prenant en charge le fret maritime et l'assurance
              pendant le transport. <Bold>Le prix CIF est donc supérieur au prix FOB</Bold> du même montant de fret + assurance.
            </p>
          </div>
        </div>

        <Sub title="Impact concret sur votre commission">
          <p>Si votre commission est de 2 % et que le fret maritime représente 15 $/tonne et l'assurance 0,5 % :</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            <div className="border border-white/10 bg-[#0c0c0c] p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">FOB · 50 000 t ciment à 60 $/t</div>
              <div className="mt-4 space-y-2 text-sm font-mono">
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Prix FOB total</span><span className="text-white">3 000 000 $</span></div>
                <div className="flex justify-between pt-3 border-t border-white/10"><span className="text-[#D4AF37]">Votre commission à 2 %</span><span className="text-[#D4AF37] font-bold">60 000 $</span></div>
              </div>
            </div>
            <div className="border border-[#D4AF37] bg-[#D4AF37]/5 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Le même deal en CIF</div>
              <div className="mt-4 space-y-2 text-sm font-mono">
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Fret total : 50 000 × 15 $</span><span className="text-white">750 000 $</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Assurance</span><span className="text-white">~ 18 750 $</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Prix CIF total</span><span className="text-white">3 768 750 $</span></div>
                <div className="flex justify-between pt-3 border-t border-[#D4AF37]/30"><span className="text-[#D4AF37]">Votre commission à 2 %</span><span className="text-[#D4AF37] font-bold">75 375 $</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Différence en votre faveur</span><span className="text-[#25D366] font-bold">+ 15 375 $</span></div>
              </div>
            </div>
          </div>

          <Callout icon={ArrowRight} title="Conseil pratique">
            <p>
              Négociez toujours votre commission en <Bold>pourcentage du montant CIF</Bold> lorsque vous êtes du côté
              vendeur. Cela maximise légitimement votre rémunération sans modifier quoi que ce soit au deal réel pour les parties.
            </p>
          </Callout>
        </Sub>
      </Section>
    </div>
  );
}

// =============================================================
// EN
// =============================================================
function PricingEN() {
  return (
    <div data-testid="view-pricing" className="max-w-5xl">
      <Hero
        moduleNum="04"
        eyebrowLabel="Module"
        icon={LineChart}
        italic="Pricing"
        accent="& valuation"
        description="Reading the markets is a skill most beginner brokers don't have. Without it, you can't tell whether a deal is realistic, negotiate prices effectively, or detect scams built on fictitious prices."
      />

      <Section eyebrow="Spot prices" title="Reading spot prices" index="4.1">
        <Sub title="What is the spot price?">
          <p>
            The spot price (or cash price) is the price at which a commodity can be bought or sold for
            <Bold> immediate delivery</Bold> — typically within 2 business days. It represents the real-time market value,
            reflecting instantaneous supply and demand. Not to be confused with futures prices, which apply to deliveries
            on a defined future date and are used mainly to hedge price variations.
          </p>
        </Sub>

        <Sub title="Price sources by commodity">
          <ul className="space-y-3">
            <li><Bold>Gold and precious metals:</Bold> London Bullion Market Association (lbma.org.uk). Publishes two
            fixings per day (AM and PM, weekdays). The LBMA Gold Price PM is the global benchmark used in all serious
            contracts. Price expressed in USD per troy ounce (1 troy ounce = 31.1035 grams).</li>
            <li><Bold>Silver:</Bold> same LBMA source. One fixing per day (PM).</li>
            <li><Bold>Platinum and palladium:</Bold> London Platinum and Palladium Market (lppm.com).</li>
            <li><Bold>Brent crude oil</Bold> (European and global benchmark): ICE Futures Europe. Price in USD per barrel
            (1 barrel = 158.99 litres). Platts (S&P Global) and Argus Media are the professional references. Bloomberg
            and Reuters relay these prices in real time.</li>
            <li><Bold>WTI crude oil</Bold> (US benchmark): NYMEX (CME Group). Slightly different from Brent depending on
            market conditions.</li>
            <li><Bold>Industrial metals</Bold> (copper, aluminum, zinc, lead, nickel, tin): London Metal Exchange
            (lme.com). Closely watched 3-month official quotations. Price in USD per metric ton.</li>
            <li><Bold>Agricultural products:</Bold> wheat, corn, soybeans on CBOT (Chicago Board of Trade, CME group).
            Arabica coffee and cocoa on ICE Futures US. Raw sugar on ICE Futures US. Robusta coffee on ICE Futures Europe
            (London).</li>
            <li><Bold>Cement:</Bold> no centralised global listed market. Prices are regional and tracked through Global
            Cement Magazine, CemNet, CemBureau, or Platts Cement indices for certain regions.</li>
            <li><Bold>Gemstones (diamonds):</Bold> Rapaport Price List (rappaport.com). Weekly publication in USD per
            carat by quality (cut, color, clarity, carat — the 4Cs). For colored stones (emeralds, rubies, sapphires):
            GemGuide or prices published by auction houses (Sotheby's, Christie's).</li>
            <li><Bold>Natural gas:</Bold> Henry Hub (NYMEX, North-American benchmark). TTF (Title Transfer Facility,
            European benchmark, listed on ICE). NBP (National Balancing Point, UK benchmark).</li>
          </ul>
        </Sub>

        <Sub title="How to read a Bloomberg or Reuters quote">
          <ul className="space-y-2">
            <li><Bold>Last Price (or Last):</Bold> the last price at which a transaction occurred.</li>
            <li><Bold>Bid:</Bold> the price at which a buyer is ready to buy immediately.</li>
            <li><Bold>Ask (or Offer):</Bold> the price at which a seller is ready to sell immediately.</li>
            <li><Bold>Spread:</Bold> the difference between Bid and Ask. A narrow spread means a liquid market. A wide
            spread means an illiquid or stressed market.</li>
            <li><Bold>High / Low:</Bold> the highest and lowest of the day. Useful to gauge intraday volatility.</li>
            <li><Bold>Volume:</Bold> the number of contracts or lots traded. High volume confirms liquidity and the
            validity of the prices.</li>
            <li><Bold>Change (or % Change):</Bold> variation from the previous close, in absolute and percentage terms.</li>
            <li><Bold>Open Interest</Bold> (futures markets only): the total number of open contracts (neither expired
            nor liquidated). Rising Open Interest signals growing market interest at this price level.</li>
          </ul>
        </Sub>
      </Section>

      <Section eyebrow="Premiums & discounts" title="Understanding premiums and discounts" index="4.2">
        <Sub title="Definition">
          <p>
            The spot price is a standard theoretical reference. In practice, almost all transactions occur at a
            <Bold> premium</Bold> or a <Bold>discount</Bold> to that reference, depending on multiple factors related to
            quality, logistics, the local market, and the conditions of the transaction.
          </p>
        </Sub>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <div className="border border-[#25D366]/30 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><TrendingUp className="h-5 w-5 text-[#25D366]" /><h4 className="font-serif text-lg text-[#25D366]">Factors creating a premium (+)</h4></div>
            <ul className="mt-5 space-y-3 text-sm text-[#C4C4C7]">
              <li><Bold>Quality above the reference standard:</Bold> 99.99% pure copper trades at a premium versus LME
              Grade A copper (99.97% standard). Likewise, 999.9/1000 gold trades at a premium versus 995/1000 gold.</li>
              <li><Bold>Certification and traceability:</Bold> Fairmined-certified gold (from certified responsible
              artisanal mines) trades at a significant premium in some European and North-American markets.</li>
              <li><Bold>Delivery to a specific site or shorter lead time:</Bold> if you can deliver CIF into a hard-to-
              access port or faster than standard, you can justify a premium.</li>
              <li><Bold>Tight local market:</Bold> if demand exceeds supply in a specific region, local prices rise
              above the global spot.</li>
              <li><Bold>Additional guarantees:</Bold> an SBLC bank guarantee or premium cargo insurance can justify a
              premium on the base price.</li>
            </ul>
          </div>
          <div className="border border-[#FF3B30]/30 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><TrendingDown className="h-5 w-5 text-[#FF3B30]" /><h4 className="font-serif text-lg text-[#FF3B30]">Factors creating a discount (−)</h4></div>
            <ul className="mt-5 space-y-3 text-sm text-[#C4C4C7]">
              <li>Quality slightly below the reference standard.</li>
              <li><Bold>High-risk destination:</Bold> a delivery into a port or country with logistical complications,
              high port fees, or strong political risk.</li>
              <li>Large volumes that take time to be absorbed by the market.</li>
              <li>Long intermediary chain raising the perceived counterparty risk.</li>
              <li><Bold>Uncertainty on origin or traceability:</Bold> a product without a clear certificate of origin
              trades at a discount.</li>
            </ul>
          </div>
        </div>

        <Sub title="Full calculation example for 1 ton of gold">
          <div className="border border-[#D4AF37]/30 bg-[#0c0c0c] p-6 md:p-8 font-mono text-sm">
            <div className="space-y-3">
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Today's LBMA spot price</span><span className="text-white">$2,350 / troy oz</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Conversion to USD/kg</span><span className="text-white">2,350 ÷ 31.1035 × 1,000 = $75,560/kg</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Base price · 1 ton (1,000 kg)</span><span className="text-white">$75,560,000</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Quality + certification premium (+1.5%)</span><span className="text-[#25D366]">+ $1,133,400</span></div>
              <div className="flex justify-between"><span className="text-[#A1A1AA]">Broker commission (1%)</span><span className="text-[#D4AF37]">+ $756,934</span></div>
              <div className="h-px bg-white/10 my-3" />
              <div className="flex justify-between font-bold"><span className="text-white">Total CIF buyer price</span><span className="text-[#D4AF37]">~ $78,450,000</span></div>
            </div>
          </div>
          <p className="mt-4 italic text-sm">
            Concretely, on this deal, a 1% commission represents about <Bold>$756,934</Bold> for a one-ton gold deal.
            That's why this sector attracts so many scammers: amounts are huge and margins look very attractive.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Incoterms" title="Impact of FOB vs CIF on the broker's commission" index="4.3">
        <p>
          Choosing the right incoterm isn't just a logistics question — it's also a decision that
          <Bold> directly impacts the size of your commission</Bold>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><Ship className="h-5 w-5 text-[#D4AF37]" /><span className="font-mono text-xl text-[#D4AF37]">FOB</span></div>
            <p className="font-serif text-sm text-[#A1A1AA] mt-1">Free On Board</p>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              The seller delivers the goods on board the vessel at the port of loading. Sea freight and voyage insurance
              are at the buyer's expense. <Bold>The FOB price therefore does not include these costs.</Bold>
            </p>
          </div>
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <div className="flex items-center gap-3"><Ship className="h-5 w-5 text-[#D4AF37]" /><span className="font-mono text-xl text-[#D4AF37]">CIF</span></div>
            <p className="font-serif text-sm text-[#A1A1AA] mt-1">Cost, Insurance, Freight</p>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              The seller delivers the goods to the destination port, taking sea freight and insurance during transit on
              their account. <Bold>The CIF price is therefore higher than the FOB price</Bold> by the same freight + insurance amount.
            </p>
          </div>
        </div>

        <Sub title="Concrete impact on your commission">
          <p>If your commission is 2% and sea freight is $15/ton with insurance at 0.5%:</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            <div className="border border-white/10 bg-[#0c0c0c] p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">FOB · 50,000 t cement at $60/t</div>
              <div className="mt-4 space-y-2 text-sm font-mono">
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Total FOB price</span><span className="text-white">$3,000,000</span></div>
                <div className="flex justify-between pt-3 border-t border-white/10"><span className="text-[#D4AF37]">Your 2% commission</span><span className="text-[#D4AF37] font-bold">$60,000</span></div>
              </div>
            </div>
            <div className="border border-[#D4AF37] bg-[#D4AF37]/5 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">The same deal in CIF</div>
              <div className="mt-4 space-y-2 text-sm font-mono">
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Total freight: 50,000 × $15</span><span className="text-white">$750,000</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Insurance</span><span className="text-white">~ $18,750</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Total CIF price</span><span className="text-white">$3,768,750</span></div>
                <div className="flex justify-between pt-3 border-t border-[#D4AF37]/30"><span className="text-[#D4AF37]">Your 2% commission</span><span className="text-[#D4AF37] font-bold">$75,375</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Difference in your favor</span><span className="text-[#25D366] font-bold">+ $15,375</span></div>
              </div>
            </div>
          </div>

          <Callout icon={ArrowRight} title="Practical tip">
            <p>
              Whenever you're on the seller side, always negotiate your commission as a <Bold>percentage of the CIF
              amount</Bold>. It legitimately maximises your fee without changing anything about the actual deal for the parties.
            </p>
          </Callout>
        </Sub>
      </Section>
    </div>
  );
}
